import {
  Checkbox,
  FormControlLabel,
  Stack,
  Tooltip,
  Typography
} from '@mui/material'
import React, { type CSSProperties } from 'react'

import Icon from 'components/Icon'
import { KubernetesListItemTags } from 'components/services/create/form-controls/fingerprint/kubernetes/KubernetesListItemTags'
import theme from 'theme'

import type {
  K8sAutocompleteSearchOption,
  K8sTagLabelOptionSelectionChangeHandler
} from 'components/services/create/form-controls/fingerprint/kubernetes/types'

const FormControlLabelOutput: React.FC<{
  label: string
  option: K8sAutocompleteSearchOption
}> = ({ label, option }) => {
  return (
    <Stack direction="row" sx={{ alignItems: 'center' }}>
      <Tooltip arrow placement="top" title={label}>
        <Typography
          noWrap
          color="text.secondary"
          sx={(muiTheme) => ({
            [muiTheme.breakpoints.down('lg')]: {
              maxWidth: 100,
              textOverflow: 'ellipsis'
            },
            [muiTheme.breakpoints.down('xl')]: {
              maxWidth: 150,
              textOverflow: 'ellipsis'
            }
          })}
          variant="body2"
        >
          {label}
        </Typography>
      </Tooltip>
      {option.owner.isOwner ? null : (
        <Tooltip
          arrow
          title={`This object is shared by Team, "${option.owner.teamName}" due to being in a shared namespace. Running a test on a service created with this object may impact that Team’s services as well.`}
        >
          <Icon
            color={theme.colors.orange[0]}
            icon="solidLink"
            style={{ marginLeft: theme.space[2] }}
          />
        </Tooltip>
      )}
    </Stack>
  )
}

export const KubernetesTagLabelSectionListItem: React.FC<{
  option: K8sAutocompleteSearchOption
  isSelected: boolean
  style: CSSProperties
  handleSelectOption: K8sTagLabelOptionSelectionChangeHandler
}> = ({ option, isSelected, style, handleSelectOption }) => {
  const { label, tags, uid } = option

  return (
    <Stack
      direction="row"
      role="option"
      sx={(muiTheme) => ({
        ...style,
        alignItems: 'center',
        borderBottom: `1px solid ${muiTheme.palette.grey[200]}`,
        columnGap: 2
      })}
    >
      <FormControlLabel
        control={
          <Checkbox
            checked={isSelected}
            data-test-target-option={label || uid}
            data-test-target-param-value={label || uid}
            inputProps={
              {
                'data-testid': `kubernetes-target-checkbox-${label}-${uid}`
              } as React.InputHTMLAttributes<HTMLInputElement>
            }
            onChange={(e) => handleSelectOption(e, option)}
          />
        }
        label={<FormControlLabelOutput label={label} option={option} />}
        sx={{ flexGrow: 1 }}
      />
      <KubernetesListItemTags isSelected={isSelected} tags={tags} />
    </Stack>
  )
}
